import './styles.scss'

import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Collapse, Empty } from 'antd'

import B2becAnchor from '@/components/B2becAnchor'
import B2becCollapse from '@/components/B2becCollapse'
import B2BProductCarousel from '@/components/B2BProductCarousel'
import CustomButton from '@/components/CustomButton'
import ProductComparisonModal from '@/components/ProductComparisonModal'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectAccessoryAllAmount,
  selectAccessoryList,
  selectAccessoryLoading,
  selectDetergentAllAmount,
  selectDetergentList,
  selectDetergentLoading,
  selectProductAllAmount,
  selectProductList,
  selectProductLoading,
} from '@/store/selectors/searchSelector'
import { requestSearch } from '@/store/slices/searchSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import useProductComparison from '@/hooks/useProductComparison'
import { ALL_SEARCH_RESULTS_PER_SECTION, SEARCH_RESULTS_PER_SECTION_SIZE } from '@/libs/constants'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { linkGenerator } from '@/libs/utils/language'

const { Panel } = Collapse

const AVAILABLE_PRODUCT_TYPES = ['machines', 'accessories', 'detergents']

const createSectionData = (panelData, dataList, allDataAmount) => {
  if (!allDataAmount) {
    return null
  }

  return {
    panelData,
    dataList,
    allDataAmount,
  }
}

const SearchResultsPage = ({ breadcrumbs }) => {
  const { searchParam } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useDocumentTitle('pageTitle.searchResults')
  useBreadcrumbs(breadcrumbs, [{ pathData: [searchParam] }])

  useAdobeAnalysis(TRACK_DATA.ONSITE, {
    pageId: '14',
    pageType: PAGE_TYPES.SEARCH_RESULTS,
    pageName: 'AllSearchResults',
  })

  const numberOfSkeleton = 3
  const heightOfSkeleton = 314
  const pageSize = ALL_SEARCH_RESULTS_PER_SECTION
  const pageNumber = 1

  const {
    showComparisonModal,
    setShowComparisonModal,
    onCompareHandler,
    productInformation,
    comparingProductIds,
  } = useProductComparison()

  const onCheckHandler = useCallback(
    (product, event) => {
      const isChecked = event?.target?.checked

      onCompareHandler(isChecked, product)
    },
    [onCompareHandler],
  )

  useEffect(() => {
    if (searchParam) {
      dispatch(
        requestSearch({
          searchAll: false,
          value: searchParam,
          size: pageSize,
          from: pageSize * (pageNumber - 1),
        }),
      )
    }
  }, [dispatch, searchParam, pageSize])

  const {
    isProductLoading,
    isAccessoryLoading,
    isDetergentLoading,
    machinesList,
    accessoriesList,
    detergentsList,
    machinesAllAmounts,
    accessoryAllAmount,
    detergentAllAmount,
  } = useAppSelector((state) => ({
    // loading status
    isProductLoading: selectProductLoading(state),
    isAccessoryLoading: selectAccessoryLoading(state),
    isDetergentLoading: selectDetergentLoading(state),
    // list data
    machinesList: selectProductList(state),
    accessoriesList: selectAccessoryList(state),
    detergentsList: selectDetergentList(state),
    // all amount
    machinesAllAmounts: selectProductAllAmount(state),
    accessoryAllAmount: selectAccessoryAllAmount(state),
    detergentAllAmount: selectDetergentAllAmount(state),
  }), shallowEqual)

  const availableData = AVAILABLE_PRODUCT_TYPES.reduce((result, c) => {
    let data = {}
    if (c === 'machines') {
      data = createSectionData(c, machinesList, machinesAllAmounts)
    }

    if (c === 'accessories') {
      data = createSectionData(c, accessoriesList, accessoryAllAmount)
    }

    if (c === 'detergents') {
      data = createSectionData(c, detergentsList, detergentAllAmount)
    }

    if (data !== null) {
      return { ...result, [c]: data }
    }

    return result
  }, {})

  const listKeys = Object.keys(availableData)

  const listTranslatedKeys = listKeys.map((key) => ({
    key,
    translationKey: `searchResultsPage.header.${key}`,
  }))

  const listData = Object.values(availableData)

  const onShowAllHandler = (productType) => {
    navigate(linkGenerator(`/search-detail/${productType}/${searchParam}`))
  }

  const renderSection = (panelData, dataList, allDataAmount) => {
    let content = <Empty />
    if (dataList) {
      content = (
        <div className="mb-4">
          <B2BProductCarousel
            isCompareCheckboxDisplayed
            className="product-card__wrapper mb-5"
            comparingProductIds={comparingProductIds}
            listItems={dataList}
            productsPerPage={SEARCH_RESULTS_PER_SECTION_SIZE}
            productLimitPerSection={Math.min(dataList.length, ALL_SEARCH_RESULTS_PER_SECTION)}
            onCheckHandler={onCheckHandler}
          />
          <div className="search-results__button-wrapper">
            <CustomButton
              type="ghost"
              onClick={() => onShowAllHandler(panelData)}
            >
              {t(`searchResultsPage.button.showAll.${panelData}`, {
                count: allDataAmount,
              })}
            </CustomButton>
          </div>
        </div>
      )
    }

    return (
      allDataAmount > 0 && (
        <Panel
          key={panelData}
          header={t(`searchResultsPage.header.${panelData}`)}
          id={panelData}
        >
          {content}
        </Panel>
      )
    )
  }

  return searchParam
      && (isProductLoading || isDetergentLoading || isAccessoryLoading)
    ? (
      <div style={{ marginTop: '50px' }}>
        <B2becCardSkeleton
          columns={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 8 }}
          imageHeight={heightOfSkeleton}
          number={numberOfSkeleton}
        />
      </div>
    )
    : listTranslatedKeys.length > 0
    ? (
      <>
        <div className="container-full-width-page">
          <div className="search-results">
            <div className="search-results__header">
              <div className="search-results__header__title">
                {t('searchResultsPage.title', {
                  count: +machinesAllAmounts
                    + +accessoryAllAmount
                    + +detergentAllAmount,
                }).trim()} <q>{searchParam}</q>
              </div>
            </div>
            <B2becAnchor listKeys={listTranslatedKeys} />

            <div className="search-results__content">
              <B2becCollapse
                bordered
                ghost
                defaultActiveKey={listKeys}
                expandIconPosition="end"
              >
                {listData.map(({ panelData, dataList, allDataAmount }) =>
                  renderSection(panelData, dataList, allDataAmount)
                )}
              </B2becCollapse>
            </div>
          </div>
        </div>

        <ProductComparisonModal
          productsData={productInformation}
          setShowModal={setShowComparisonModal}
          showModal={showComparisonModal}
        />
      </>
    )
    : (
      <div className="empty-content__wrapper">
        <Empty />
      </div>
    )
}

export default SearchResultsPage
